export type RelayConnection<T> = {
    edges: Array<Edge<T>>;
    pageInfo: RelayPageInfo;
};

export type Edge<T> = {
    node: Node<T>,
    cursor: string,
};

export type Node<T> = {
    id: string;
} & T;

export type RelayPageInfo = {
    hasPreviousPage: boolean;
    endCursor: string;
    hasNextPage: boolean;
    startCursor: string;
};

export type SelectOption = {
    value: string;
    label: string;
}

export type Paths<T, D extends number = 10> = [D] extends [never] ? never : T extends object ?
    {
        [K in keyof T]-?: K extends string | number ?
        `${K}` | Join<K, Paths<T[K], Prev[D]>>
        : never
    }[keyof T] : ""

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
    11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]];

type Join<K, P> = K extends string | number ?
    P extends string | number ?
        `${K}${"" extends P ? "" : "."}${P}`
        : never : never;

export type Leaves<T, D extends number = 10> = [D] extends [never] ? never : T extends object ?
    { [K in keyof T]-?: Join<K, Leaves<T[K], Prev[D]>> }[keyof T] : "";

type NameValue = {
    name: string;
    value: string;
}

const EnumToNameValues = <T, >(t: T): NameValue[] => Object.keys(t).map(key => {
    return {value: t[key as keyof typeof t] as unknown as string, name: key}
})

const NameValuesToMapValueByName = (maps: NameValue[]) => new Map(
    maps.map(x => [x.value, x.name] as [string, string])
)

const NameValueToMapNameByValue = (maps: NameValue[]) => new Map(
    maps.map(x => [x.name, x.value] as [string, string])
)

export const EnumToMapping = <T, >(t: T, defaultVal?: { label: string; value: string }, sortByValue?: boolean) => {
    let nameValues = EnumToNameValues(t);
    const sort = sortByValue ?? false;
    if (sort) {
        nameValues = nameValues.sort((a, b) => a.value.localeCompare(b.value));
    }
    const valueToName = NameValuesToMapValueByName(nameValues);
    const nameToValue = NameValueToMapNameByValue(nameValues);
    let nameToValueWithDefault = nameToValue;
    if (defaultVal) {
        nameToValueWithDefault = new Map<string, string>([
            [defaultVal.value, defaultVal.label],
            ...nameToValue.entries(),
        ])
    }
    return {
        names: nameValues.map((nv) => nv.name),
        valueToName,
        nameToValue,
        nameToValueWithDefault
    };
}

type StringEnum = { [key: string]: string };

export function getEnumName<T extends StringEnum>(enumObj: T, value: string): keyof T | null {
    for (const prop in enumObj) {
        if (enumObj[prop as keyof T] === value) {
            return prop as keyof T;
        }
    }
    return null;
}

export function getEnumNameValueMap<T extends StringEnum>(enumObj: T, value: string): Map<string, string>{
    const name = getEnumName(enumObj, value);
    if (name) {
        return new Map([[name as string, value]]);
    }

    return new Map();
}


// Define a helper type to obtain deep keys of a type (nested keys)
export type DeepKeyof<T, D extends string = "."> =
    T extends string
        ? T
        : T extends object
            ? {
                [K in keyof T]-?: K extends string
                    ? K | `${K extends "" ? "" : D}${DeepKeyof<T[K], D>}`
                    : never;
            }[keyof T]
            : never;
